import { useLocalSearchParams, useRouter } from 'expo-router'
import { Mail, Send } from 'lucide-react-native'
import { useState } from 'react'
import { Linking, Platform, StyleSheet, View } from 'react-native'
import { Card, Text } from 'react-native-paper'
import { useSemanticColors } from '@/constants/SemanticColors'
import { useAvoidSoftInputForScreen } from '@/hooks/useAvoidSoftInputForScreen'
import { useNavigateBack } from '@/hooks/useNavigateBack'
import { useQuotaCheck } from '@/hooks/useUsage'
import { errorToString } from '@/lib/errors'
import { notify } from '@/lib/notice'
import { PB_SERVER_ADDR, pb } from '@/lib/pocketbase'
import { Button } from '@/ui/Button'
import { UpgradePrompt } from '@/ui/billing'
import { SelectInput, TextInput, useForm, z, zodResolver } from '@/ui/form'
import { Page } from '@/ui/Page'

const inviteSchema = z.object({
    email: z
        .string()
        .email('Please enter a valid email address')
        .min(1, 'Email address is required'),
    name: z.string().min(1, 'Name is required'),
    role: z.enum(['admin', 'clerical', 'workforce'], {
        message: 'Please select a valid role',
    }),
})

type InviteFormData = z.infer<typeof inviteSchema>

export default function InviteMemberPage() {
    useAvoidSoftInputForScreen()
    const params = useLocalSearchParams<{ orgSlug: string }>()
    const router = useRouter()
    const { navigateBackTo } = useNavigateBack()
    const [isPending, setIsPending] = useState(false)
    const [isSuccess, setIsSuccess] = useState(false)
    const [isError, setIsError] = useState(false)
    const [showUpgradePrompt, setShowUpgradePrompt] = useState(false)
    const { canAddTeamMember } = useQuotaCheck()
    const semanticColors = useSemanticColors()

    const {
        control,
        handleSubmit,
        formState: { isValid },
    } = useForm<InviteFormData>({
        resolver: zodResolver(inviteSchema),
        defaultValues: {
            email: '',
            name: '',
            role: 'clerical' as const,
        },
    })

    const inviteMember = async (data: InviteFormData) => {
        setIsPending(true)
        setIsError(false)
        try {
            const response = await fetch(`${PB_SERVER_ADDR}/api/invite`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${pb.authStore.token}`,
                },
                body: JSON.stringify({ ...data, org_slug: params.orgSlug }),
            })
            if (!response.ok) {
                const errorData = await response
                    .json()
                    .catch(() => ({ message: 'An unexpected error occurred' }))
                throw new Error(errorData.message || 'invite failed')
            }
            setIsSuccess(true)
            setTimeout(() => {
                navigateBackTo(`/${params.orgSlug}/settings/team`)
            }, 2000)
        } catch (error) {
            const message = errorToString(error)
            notify.error(message)
            setIsError(true)
        } finally {
            setIsPending(false)
        }
    }

    const onSubmit = (data: InviteFormData) => {
        if (!canAddTeamMember()) {
            setShowUpgradePrompt(true)
            return
        }
        inviteMember(data)
    }

    const handleUpgrade = () => {
        setShowUpgradePrompt(false)
        if (Platform.OS === 'ios') {
            Linking.openURL(`https://jobtimey.com/${params.orgSlug}/settings/billing`)
        } else {
            router.push(`/${params.orgSlug}/settings/billing`)
        }
    }

    if (isSuccess) {
        return (
            <Page>
                <Card
                    style={[
                        styles.successCard,
                        {
                            backgroundColor: semanticColors.successLight,
                            borderColor: semanticColors.successBorder,
                        },
                    ]}
                >
                    <Card.Content style={styles.successContent}>
                        <View
                            style={[
                                styles.successIconContainer,
                                { backgroundColor: semanticColors.success },
                            ]}
                        >
                            <Send size={32} color={semanticColors.successLight} />
                        </View>
                        <View style={styles.successTextContainer}>
                            <Text
                                variant="headlineSmall"
                                style={{ color: semanticColors.successText, fontWeight: '600' }}
                            >
                                Invitation Sent!
                            </Text>
                            <Text
                                variant="bodyMedium"
                                style={{ color: semanticColors.successText, textAlign: 'center' }}
                            >
                                Invitation has been sent successfully
                            </Text>
                            <Text
                                variant="bodySmall"
                                style={{ color: semanticColors.successText, textAlign: 'center' }}
                            >
                                Redirecting to team list...
                            </Text>
                        </View>
                    </Card.Content>
                </Card>
            </Page>
        )
    }

    return (
        <Page>
            {isError && (
                <Card
                    style={[
                        styles.errorCard,
                        {
                            backgroundColor: semanticColors.errorLight,
                            borderColor: semanticColors.errorBorder,
                        },
                    ]}
                >
                    <Card.Content>
                        <Text style={{ color: semanticColors.errorText }}>
                            Failed to send invitation. Please try again.
                        </Text>
                    </Card.Content>
                </Card>
            )}

            <Card style={styles.formCard}>
                <Card.Content>
                    <View style={styles.formContent}>
                        <Text variant="titleMedium" style={styles.sectionTitle}>
                            Member Details
                        </Text>

                        <View style={styles.inputSection}>
                            <Text variant="labelLarge" style={styles.inputLabel}>
                                Full Name *
                            </Text>
                            <TextInput
                                name="name"
                                control={control}
                                placeholder="Enter full name"
                                autoCapitalize="words"
                            />
                        </View>

                        <View style={styles.inputSection}>
                            <Text variant="labelLarge" style={styles.inputLabel}>
                                Email Address *
                            </Text>
                            <TextInput
                                name="email"
                                control={control}
                                placeholder="Enter email address"
                                keyboardType="email-address"
                                autoCapitalize="none"
                                autoCorrect={false}
                            />
                        </View>

                        <View style={styles.inputSection}>
                            <SelectInput<InviteFormData>
                                name="role"
                                horizontal
                                control={control}
                                label="Role *"
                                options={[
                                    { label: 'Admin', value: 'admin' },
                                    { label: 'Clerical', value: 'clerical' },
                                    { label: 'Workforce', value: 'workforce' },
                                ]}
                            />
                            <Text variant="bodySmall" style={{ color: semanticColors.helpText }}>
                                Clerical staff can view and edit most data. Admins can manage team
                                members and settings. Workforce has restricted access.
                            </Text>
                        </View>

                        <View style={styles.buttonSection}>
                            <Button
                                title="Send Invitation"
                                mode="contained"
                                onPress={handleSubmit(onSubmit)}
                                disabled={isPending || !isValid}
                                loading={isPending}
                                loadingTitle="Sending..."
                                icon="send"
                                style={styles.submitButton}
                            />
                        </View>

                        <Text variant="bodySmall" style={{ color: semanticColors.helpText }}>
                            The person will receive an email invitation to join your organization.
                        </Text>
                    </View>
                </Card.Content>
            </Card>

            <Card
                style={[
                    styles.infoCard,
                    {
                        backgroundColor: semanticColors.infoLight,
                        borderColor: semanticColors.infoBorder,
                    },
                ]}
            >
                <Card.Content>
                    <View style={styles.infoContent}>
                        <View style={styles.infoHeader}>
                            <Mail size={16} color={semanticColors.infoText} />
                            <Text
                                variant="titleSmall"
                                style={{ color: semanticColors.infoText, fontWeight: '600' }}
                            >
                                How Invitations Work
                            </Text>
                        </View>
                        <View style={styles.infoBullets}>
                            <Text variant="bodySmall" style={{ color: semanticColors.infoText }}>
                                • The invited person will receive an email with a link to join your
                                organization
                            </Text>
                            <Text variant="bodySmall" style={{ color: semanticColors.infoText }}>
                                • Invitations expire after 7 days
                            </Text>
                        </View>
                    </View>
                </Card.Content>
            </Card>

            <UpgradePrompt
                type="team"
                visible={showUpgradePrompt}
                onDismiss={() => setShowUpgradePrompt(false)}
                onUpgrade={handleUpgrade}
            />
        </Page>
    )
}

const styles = StyleSheet.create({
    cancelButton: {
        alignSelf: 'flex-start',
    },
    successCard: {
        borderWidth: 1,
    },
    successContent: {
        alignItems: 'center',
        gap: 16,
    },
    successIconContainer: {
        width: 64,
        height: 64,
        borderRadius: 32,
        alignItems: 'center',
        justifyContent: 'center',
    },
    successTextContainer: {
        alignItems: 'center',
        gap: 8,
    },
    errorCard: {
        borderWidth: 1,
    },
    formCard: {
        borderWidth: 1,
    },
    formContent: {
        gap: 16,
    },
    sectionTitle: {
        fontWeight: '600',
    },
    inputSection: {
        gap: 8,
    },
    inputLabel: {
        fontWeight: '600',
    },
    inputRow: {
        flexDirection: 'row',
        gap: 12,
        alignItems: 'flex-end',
    },
    inputContainer: {
        flex: 1,
    },
    buttonSection: {
        marginTop: 8,
    },
    submitButton: {
        alignSelf: 'flex-start',
        minWidth: 150,
    },
    infoCard: {
        borderWidth: 1,
    },
    infoContent: {
        gap: 12,
    },
    infoHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    infoBullets: {
        gap: 8,
    },
})
